import { functions } from "../firebase";
import {
  OutreachMetrics,
  OutreachLead,
  StageDef,
  OutreachDraft,
  DraftMessage,
  OutreachRunSummary,
  InstagramSessionHealth,
  InstagramLeadSequence,
  OutreachRailSnapshot,
  OutreachAnalysis,
  OutreachAnalysisFinding,
} from "../../types/OutreachMetrics";

// There is no default outreach account. Every call must name the community
// (account id == community id) it operates on; the callables reject requests
// without one.

export interface OutreachAccountSummary {
  id: string; // community id
  leadCount: number;
}

// Which communities actually have an outreach board — feeds the account picker
// on the Messaging screen. Sorted by lead count (busiest board first).
export const listOutreachAccounts = async (): Promise<
  OutreachAccountSummary[]
> => {
  const callable = functions.httpsCallable("listOutreachAccounts");
  const result = await callable({});
  const { accounts } = (result.data || {}) as {
    accounts: OutreachAccountSummary[];
  };
  return accounts ?? [];
};

// Reads accounts/{accountId}/outreach/latest via the Admin SDK (the callable
// bypasses Firestore rules, which are a stub in this repo). Returns null when no
// snapshot has been synced yet.
export const getOutreachMetrics = async (
  accountId: string
): Promise<OutreachMetrics | null> => {
  const callable = functions.httpsCallable("getOutreachMetrics");
  const result = await callable({ accountId });
  const { metrics } = (result.data || {}) as { metrics: OutreachMetrics | null };
  return metrics ?? null;
};

// Lightweight list (no conversation/touches arrays) for the board. The client
// derives the Today / Leads / Pipeline views from the full set.
export const getOutreachLeads = async (
  accountId: string
): Promise<OutreachLead[]> => {
  const callable = functions.httpsCallable("getOutreachLeads");
  const result = await callable({ accountId });
  const { leads } = (result.data || {}) as { leads: OutreachLead[] };
  return leads ?? [];
};

// Full lead incl. conversation + touches — loaded when the profile drawer opens.
export const getOutreachLead = async (
  leadId: string,
  accountId: string
): Promise<OutreachLead | null> => {
  const callable = functions.httpsCallable("getOutreachLead");
  const result = await callable({ accountId, leadId });
  const { lead } = (result.data || {}) as { lead: OutreachLead | null };
  return lead ?? null;
};

// Inline write actions (advance stage / snooze / mark done / set next date).
// Server whitelists which fields may change. Optional `note` appends a touch.
export const updateOutreachLead = async (params: {
  leadId: string;
  patch: Partial<OutreachLead>;
  note?: string;
  accountId: string;
}): Promise<OutreachLead> => {
  const callable = functions.httpsCallable("updateOutreachLead");
  const result = await callable({
    accountId: params.accountId,
    leadId: params.leadId,
    patch: params.patch,
    note: params.note,
  });
  const { lead } = (result.data || {}) as { lead: OutreachLead };
  return lead;
};

// ─── Draft review queue ───────────────────────────────────────────────────────

export interface DraftListResult {
  drafts: OutreachDraft[];
  // What the daily run last did (from Outreach/{id}/state/daily_run), or null
  // if the pipeline hasn't run yet.
  lastRun: OutreachRunSummary | null;
}

// Drafts the daily run queued for human review, newest first. Optional status
// filter ("pending" | "held" | "approved" | "rejected" | "shipped" | "error").
export const listOutreachDrafts = async (
  accountId: string,
  status?: string
): Promise<DraftListResult> => {
  const callable = functions.httpsCallable("listOutreachDrafts");
  const result = await callable({ accountId, status });
  const data = (result.data || {}) as Partial<DraftListResult>;
  return { drafts: data.drafts ?? [], lastRun: data.lastRun ?? null };
};

// Approve or reject one draft, optionally editing its messages/anchor in the
// same action. Approval requires an anchor (rule zero); the server enforces it.
export const reviewOutreachDraft = async (params: {
  accountId: string;
  draftId: string;
  status: "approved" | "rejected";
  messages?: DraftMessage[];
  anchor?: string;
  reviewNote?: string;
}): Promise<OutreachDraft> => {
  const callable = functions.httpsCallable("reviewOutreachDraft");
  const result = await callable(params);
  const { draft } = (result.data || {}) as { draft: OutreachDraft };
  return draft;
};

// ─── Send-rail capacity snapshots ────────────────────────────────────────────

// Per-day rail snapshots (newest first) for the Ops capacity graph. The daily
// social routines write them through the MCP; a day with no run has no
// snapshot, so the caller must render missing days as gaps, not as zero.
export const listOutreachRailSnapshots = async (
  accountId: string,
  days = 14
): Promise<OutreachRailSnapshot[]> => {
  const callable = functions.httpsCallable("listOutreachRailSnapshots");
  const result = await callable({ accountId, days });
  const { snapshots } = (result.data || {}) as {
    snapshots: OutreachRailSnapshot[];
  };
  return snapshots ?? [];
};

// ─── Daily outreach analysis ─────────────────────────────────────────────────

// Per-day analyses (newest first) for the Analysis tab. The daily analysis
// routine writes them through the MCP; a day with no run has no document, so
// the caller must render missing days as gaps, not as zero.
export const listOutreachAnalyses = async (
  accountId: string,
  days = 30
): Promise<OutreachAnalysis[]> => {
  const callable = functions.httpsCallable("listOutreachAnalyses");
  const result = await callable({ accountId, days });
  const { analyses } = (result.data || {}) as {
    analyses: OutreachAnalysis[];
  };
  return analyses ?? [];
};

// Mark one finding done, dismissed or open again. Status is the only field the
// dashboard writes; the analysis text belongs to the routine that produced it.
export const updateOutreachAnalysisFinding = async (
  accountId: string,
  date: string,
  findingId: string,
  status: string
): Promise<OutreachAnalysisFinding> => {
  const callable = functions.httpsCallable("updateOutreachAnalysisFinding");
  const result = await callable({ accountId, date, findingId, status });
  const { finding } = (result.data || {}) as {
    finding: OutreachAnalysisFinding;
  };
  return finding;
};

// ─── Founder Instagram session health ────────────────────────────────────────

export const getInstagramSessionHealth = async (
  accountId: string
): Promise<InstagramSessionHealth> => {
  const callable = functions.httpsCallable("getInstagramSessionHealth");
  const result = await callable({ accountId });
  const { health } = (result.data || {}) as {
    health: InstagramSessionHealth;
  };
  return health;
};

export const recordInstagramSeedCheck = async (params: {
  accountId: string;
  recipientAlias: string;
  relationship: "follower" | "non_follower";
  placement:
    | "primary"
    | "requests"
    | "hidden_requests"
    | "spam"
    | "not_received"
    | "unknown";
  observedAt: string;
  note?: string;
}): Promise<{
  checkId: string;
  health: InstagramSessionHealth;
}> => {
  const callable = functions.httpsCallable("recordInstagramSeedCheck");
  const result = await callable(params);
  return (result.data || {}) as {
    checkId: string;
    health: InstagramSessionHealth;
  };
};

export const listInstagramLeadSequences = async (
  accountId: string,
  limit = 100
): Promise<InstagramLeadSequence[]> => {
  const callable = functions.httpsCallable("listInstagramLeadSequences");
  const result = await callable({ accountId, limit });
  const { sequences } = (result.data || {}) as {
    sequences: InstagramLeadSequence[];
  };
  return sequences ?? [];
};

export const controlInstagramLeadSequence = async (params: {
  accountId: string;
  sequenceId: string;
  command: "pause" | "resume" | "cancel";
}): Promise<InstagramLeadSequence> => {
  const callable = functions.httpsCallable("controlInstagramLeadSequence");
  const result = await callable(params);
  const { sequence } = (result.data || {}) as {
    sequence: InstagramLeadSequence;
  };
  return sequence;
};

// ─── Stage config (the editable pipeline) ─────────────────────────────────────

export interface StageConfigResult {
  stages: StageDef[];
  isDefault: boolean;
  updatedAt: string | null;
}

// Reads the account's pipeline stages. isDefault=true means no custom pipeline
// has been saved yet (the built-in stages are in use).
export const getOutreachStages = async (
  accountId: string
): Promise<StageConfigResult> => {
  const callable = functions.httpsCallable("getOutreachStages");
  const result = await callable({ accountId });
  const data = (result.data || {}) as Partial<StageConfigResult>;
  return {
    stages: data.stages ?? [],
    isDefault: data.isDefault ?? true,
    updatedAt: data.updatedAt ?? null,
  };
};

// Replaces the account's pipeline stages (full ordered array). Server validates
// keys/labels/tones and that at least one non-terminal stage remains.
export const setOutreachStages = async (params: {
  stages: StageDef[];
  accountId: string;
}): Promise<StageConfigResult> => {
  const callable = functions.httpsCallable("setOutreachStages");
  const result = await callable({
    accountId: params.accountId,
    stages: params.stages,
  });
  const data = (result.data || {}) as Partial<StageConfigResult>;
  return {
    stages: data.stages ?? params.stages,
    isDefault: false,
    updatedAt: data.updatedAt ?? null,
  };
};
